/**
 * One markdown property, diffed when a pending proposal changes it.
 *
 * Shared by the automation description card (ahoy_summary) and the
 * Documentation section under the Overview tab (ahoy_documentation) —
 * extracted so the diff/collapse treatment cannot drift between the two.
 */

import { CollapsibleContent } from '@/lib/base/collapsible-content';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { PropertyValue } from '@/types/crm';

import { PropertyDiffValue } from '../timeline/components/shared/PropertyDiffValue';

/** Max collapsed height ≈ 10 lines of sm text (14px × 1.5 line-height × 10). */
const COLLAPSED_MAX_HEIGHT = 210;

export interface MarkdownPropertyBlockProps {
  value: string;
  /**
   * The value this replaces, when a pending proposal changes it. `undefined`
   * means untouched — NOT "changed to nothing", which is why the caller keys
   * on the property's presence in `previousValues` rather than on truthiness.
   */
  previousValue: unknown;
  hasPrevious: boolean;
  schema: PropertyTypeSchema | undefined;
  orgId: number;
  entityProperties: Record<string, PropertyValue> | undefined;
  /** Resets the collapse state when the record or its text changes. */
  collapseKey: string;
  /**
   * Skip the collapse clamp — for hosts that already own the space control
   * (the Documentation block sits inside a CollapsibleSection, where a
   * Show-more toggle would stack two disclosure controls on one block).
   */
  noClamp?: boolean;
}

export function MarkdownPropertyBlock({
  value,
  previousValue,
  hasPrevious,
  schema,
  orgId,
  entityProperties,
  collapseKey,
  noClamp = false,
}: MarkdownPropertyBlockProps) {
  // No CollapsibleContent wrapper on the diff path: PropertyDiffValue does its
  // own clipping (PropertyDiffValue.tsx:231), so wrapping it would nest two
  // independently-measured collapsers and give the block two Show more toggles.
  if (hasPrevious) {
    return (
      <PropertyDiffValue
        oldValue={previousValue}
        newValue={value}
        schema={schema}
        orgId={orgId}
        entityProperties={entityProperties}
      />
    );
  }

  const label = (
    <PropertyValueLabel
      value={value}
      propertyType={schema}
      orgId={orgId}
      fontSize='sm'
    />
  );

  if (noClamp) {
    return label;
  }

  // <CollapsibleContent> handles the height clip + scrollHeight overflow
  // detection + fade-mask + Show more / Show less toggle in one place. A short
  // value never overflows, so no toggle appears.
  //
  // The key combines the record id + the text so the internal
  // expanded/collapsed state resets when navigating to a different automation
  // OR when the same record's text changes. Keying on the text alone would
  // leak UI state between distinct records that happen to share identical text.
  return (
    <CollapsibleContent
      key={collapseKey}
      maxCollapsedHeight={COLLAPSED_MAX_HEIGHT}
    >
      {label}
    </CollapsibleContent>
  );
}
